import cn from 'classnames'
import React, { useCallback } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import SvgUpload from 'src/components/Svgs/SvgUpload'
import { useTranslate } from 'src/localization/Trns'

import styles from './InvoiceUploader.style'

/** 10MB, enforced against the real `File.size` (matches AddAttachment/Dropzone). */
const MAX_SIZE_BYTES = 10_000_000

/* Module scope, not an inline literal: react-dropzone memoizes its accept
   parsing and the getRootProps/getInputProps it hands back on this object's
   identity. All of the flyout's form state lives in PayBillFlyout, so a fresh
   object here would re-derive all of that on every keystroke anywhere in the
   form. */
const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/svg+xml': ['.svg'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/gif': ['.gif'],
}

export interface InvoiceUploaderProps {
  file: File | null
  error: string | null
  onFileAccepted: (file: File) => void
  onFileRejected: (message: string) => void
  onRemove: () => void
  className?: string
}

/**
 * Optional invoice upload. Attaching a file does NOTHING except record it in
 * the parent's local state — no OCR, no autofill, no preview drawer, no upload
 * endpoint. We wire `react-dropzone` ourselves (the reuse-the-pattern-not-the-
 * component approach from AddAttachment/Dropzone) with an `onDrop` that only
 * lifts local state up.
 */
const InvoiceUploader: React.FC<InvoiceUploaderProps> = ({
  file,
  error,
  onFileAccepted,
  onFileRejected,
  onRemove,
  className,
}) => {
  const translate = useTranslate()

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
      // A file over 10MB (or of an unsupported type) lands in fileRejections —
      // it goes to a failed state rather than being silently accepted.
      if (fileRejections.length > 0) {
        onFileRejected(
          translate(
            "This file couldn't be attached. Use a PDF, SVG, PNG, JPG, or GIF up to 10MB.",
          ),
        )
        return
      }
      const accepted = acceptedFiles[0]
      if (accepted) onFileAccepted(accepted)
    },
    [onFileAccepted, onFileRejected, translate],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_SIZE_BYTES,
    multiple: false,
  })

  const attached = file !== null

  return (
    <div className={cn('InvoiceUploader', className)} css={styles}>
      {/* No heading here. This uploader is one of the flyout's three titled
          sections, so its title is rendered by PayBillFlyout as a real section
          heading alongside "Select Vendor" and "Invoice Details" — rather than
          as a smaller in-component title that read as the only bold thing on
          the form. Both description strings were dropped as redundant: the
          dropzone states what to do when empty, and the file row speaks for
          itself once something is attached. */}
      {attached ? (
        <div className={'uploader-file'} data-testid={'invoice-file-row'}>
          <span className={'uploader-file-icon'}>
            <SvgAttachmentFile />
          </span>
          <span className={'uploader-file-meta'}>
            <span className={'uploader-file-name ellipsis'}>{file.name}</span>
            <span className={'uploader-file-status'}>
              {translate('Attached')}
            </span>
          </span>
          <button
            type={'button'}
            className={'uploader-file-remove'}
            aria-label={translate('Remove attached invoice')}
            onClick={onRemove}
            data-testid={'invoice-file-remove'}
          >
            <SvgTrash />
          </button>
        </div>
      ) : (
        <>
          <div
            {...getRootProps({
              className: cn('uploader-dropzone', {
                'is-drag-active': isDragActive,
                'is-error': error !== null,
              }),
            })}
            data-testid={'invoice-dropzone'}
          >
            <input {...getInputProps()} data-testid={'invoice-file-input'} />
            <SvgUpload />
            <span className={'uploader-cta'}>
              <span className={'uploader-link'}>
                {translate('Click to upload')}
              </span>{' '}
              {translate('or drag and drop')}
            </span>
            <span className={'uploader-hint'}>
              {translate('PDF, SVG, PNG, JPG or GIF (max. 10MB)')}
            </span>
          </div>
          {error !== null && (
            <p className={'uploader-error'} role={'alert'}>
              {error}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default InvoiceUploader
